import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Github, Linkedin, Send, CheckCircle } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SakuraTree3D from './SakuraTree3D';
import { SECTIONS, createSectionVariants } from '../lib/motionDirector';

const Contact = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Motion Director - CTA section with clear sequence
  const variants = createSectionVariants(SECTIONS.contact);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const webhookUrl = process.env.REACT_APP_DISCORD_WEBHOOK;
      
      if (!webhookUrl) {
        console.error('Discord webhook URL not configured');
        alert('Contact form is not configured properly. Please contact via email.');
        setIsSubmitting(false);
        return;
      }

      // Create Discord embed message
      const discordPayload = {
        content: `**New Contact Form Submission**\n\n**Name:** ${formData.name}\n**Email:** ${formData.email}\n**Message:**\n${formData.message}`,
        embeds: [{
          title: "🎯 New Contact Form Submission",
          color: 0x8B0000, // Crimson color
          fields: [
            {
              name: "👤 Name",
              value: formData.name,
              inline: true
            },
            {
              name: "📧 Email",
              value: formData.email,
              inline: true
            },
            {
              name: "💬 Message",
              value: formData.message || "No message provided",
              inline: false
            }
          ],
          timestamp: new Date().toISOString(),
          footer: {
            text: "Portfolio Contact Form"
          }
        }]
      };

      // Use fetch with proper error handling
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(discordPayload),
        mode: 'no-cors' // This allows the request to complete even without CORS
      });

      // With no-cors mode, we can't read the response, so assume success
      // The Discord webhook will either succeed or fail silently
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Still show success since we can't determine the actual result with no-cors
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', message: '' });
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: Phone, label: 'Phone', value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
    { icon: MapPin, label: 'Location', value: personalInfo.location, href: null },
  ];

  const socialLinks = [
    { icon: Github, label: 'GitHub', href: `https://${personalInfo.github}` },
    { icon: Linkedin, label: 'LinkedIn', href: `https://${personalInfo.linkedin}` },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-zinc-900 overflow-hidden" ref={ref}>
      {/* Teal-blue ambient glow for variety */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-950/12 via-cyan-950/6 to-blue-950/8" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_40%,rgba(20,184,166,0.08),transparent_50%)]" />
      {/* 3D Sakura Tree Background */}
      <SakuraTree3D />
      
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-amber-100/[0.03] select-none tracking-wider">連絡</div>
      </div>
      
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 text-[15rem] font-bold text-amber-100 select-none">絡</div>
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 text-xs tracking-[0.3em] text-amber-500/80 border border-amber-900/40 rounded-full mb-4">
            SUMMON ME
          </span>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-wider text-amber-50">
            LET US FORGE TOGETHER
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div variants={variants.item}>
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.label}
                  variants={variants.item}
                  className="flex items-center gap-4 p-3 rounded-lg transition-all duration-300"
                  whileHover={{
                    boxShadow: '0 4px 12px -4px rgba(139, 0, 0, 0.3)',
                    x: 3
                  }}
                >
                  <motion.div 
                    className="p-3 bg-red-900/20 rounded-lg border border-red-900/30"
                    whileHover={{ rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <item.icon className="w-5 h-5 text-red-600" />
                  </motion.div>
                  <div>
                    <p className="text-xs tracking-wider text-amber-100/50 uppercase mb-1">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-amber-100 hover:text-amber-400 transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-amber-100">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social Links */}
            <div className="mt-10">
              <p className="text-xs tracking-wider text-amber-100/50 uppercase mb-4">Connect with me</p>
              <div className="flex gap-4">
                {socialLinks.map((link) => (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-4 bg-zinc-900/50 border border-amber-900/20 rounded-lg hover:border-amber-700/40 transition-all duration-300"
                    whileHover={{
                      boxShadow: '0 10px 30px -10px rgba(217, 119, 6, 0.4), 0 0 0 1px rgba(251, 191, 36, 0.1)',
                      borderColor: 'rgba(217, 119, 6, 0.6)'
                    }}
                  >
                    <motion.div
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <link.icon className="w-6 h-6 text-amber-100/70 group-hover:text-amber-100 transition-colors" />
                    </motion.div>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Decorative element */}
            <div className="mt-12 p-6 bg-black/50 border border-amber-900/20 rounded-lg backdrop-blur-sm">
              <p className="text-amber-100/70 italic text-center">
                "A true warrior knows when to draw the blade and when to offer a hand."
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={variants.item}>
            <form onSubmit={handleSubmit} className="space-y-6 relative z-10 p-8 bg-black/30 backdrop-blur-md border border-amber-900/30 rounded-xl">
              <div>
                <label className="block text-xs tracking-wider text-amber-100/50 uppercase mb-2">
                  Your Name
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-zinc-950/80 backdrop-blur-sm border-amber-900/30 text-amber-100 placeholder:text-amber-100/30 focus:border-amber-700/50 focus:ring-amber-700/20"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider text-amber-100/50 uppercase mb-2">
                  Your Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-zinc-950/80 backdrop-blur-sm border-amber-900/30 text-amber-100 placeholder:text-amber-100/30 focus:border-amber-700/50 focus:ring-amber-700/20"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider text-amber-100/50 uppercase mb-2">
                  Your Message
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="bg-zinc-950/80 backdrop-blur-sm border-amber-900/30 text-amber-100 placeholder:text-amber-100/30 focus:border-amber-700/50 focus:ring-amber-700/20 resize-none"
                  placeholder="Speak your mind..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitted || isSubmitting}
                className="w-full bg-red-800 hover:bg-red-700 text-amber-50 border border-red-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Send className="w-5 h-5" />
                    </motion.div>
                    Sending...
                  </span>
                ) : isSubmitted ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Message Sent!
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Contact;
